import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Home, Plus, Star, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SavedAddressesCard — Cluster 6 · P8.
 *
 * Standalone card that lists a consumer's saved delivery addresses,
 * lets them create/edit/delete, and mark one as default. Wired to
 * /api/consumer/addresses.
 *
 * Rendered inside ConsumerSettings but is self-contained so it can be
 * dropped into ConsumerCheckout later without refactoring.
 */
export default function SavedAddressesCard() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' or an address id
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/consumer/addresses');
      setAddresses(res.data?.addresses || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load addresses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing('new'); setForm(EMPTY_FORM); };
  const startEdit = (addr) => {
    setEditing(addr.id);
    setForm({
      label: addr.label || '',
      street: addr.street || '',
      apartment: addr.apartment || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      delivery_instructions: addr.delivery_instructions || '',
      is_default: !!addr.is_default,
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    const err = validate(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/consumer/addresses', form);
        toast.success('Address added');
      } else {
        const { is_default, ...patch } = form;
        await api.put(`/consumer/addresses/${editing}`, patch);
        if (is_default) {
          await api.put(`/consumer/addresses/${editing}/default`);
        }
        toast.success('Address updated');
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.put(`/consumer/addresses/${id}/default`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update default');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this saved address?')) return;
    try {
      await api.delete(`/consumer/addresses/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid="saved-addresses-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Home className="w-5 h-5 text-emerald-600" />
            <CardTitle>Saved delivery addresses</CardTitle>
          </div>
          <CardDescription>
            Save addresses to check out faster. Star one as your default.
          </CardDescription>
        </div>
        {!editing && (
          <Button size="sm" onClick={startNew} data-testid="saved-address-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && addresses.length === 0 && !editing && (
          <p className="text-sm text-slate-500" data-testid="saved-addresses-empty">
            No saved addresses yet. Add one for one-click checkout.
          </p>
        )}
        {addresses.map(a => (
          <div key={a.id} className="rounded-lg border border-slate-200 p-3 flex items-start justify-between gap-3"
               data-testid={`saved-address-row-${a.id}`}>
            {editing === a.id ? (
              <AddressForm form={form} setForm={setForm} onCancel={cancel} onSave={save} saving={saving} />
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 truncate">{a.label}</p>
                    {a.is_default && <Badge className="bg-emerald-500">Default</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    {a.street}{a.apartment ? `, ${a.apartment}` : ''}
                  </p>
                  <p className="text-sm text-slate-600">
                    {a.city}, {a.state} {a.zip}
                  </p>
                  {a.delivery_instructions && (
                    <p className="text-xs text-slate-500 mt-1 italic">&quot;{a.delivery_instructions}&quot;</p>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {!a.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => makeDefault(a.id)}
                            data-testid={`saved-address-star-${a.id}`}>
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => startEdit(a)}
                          data-testid={`saved-address-edit-${a.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                          data-testid={`saved-address-delete-${a.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
        {editing === 'new' && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <AddressForm form={form} setForm={setForm} onCancel={cancel} onSave={save} saving={saving} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const EMPTY_FORM = {
  label: '', street: '', apartment: '', city: '', state: '', zip: '',
  delivery_instructions: '', is_default: false,
};

function validate(f) {
  if (!f.label.trim()) return 'Label required (e.g., "Home").';
  if (!f.street.trim()) return 'Street required.';
  if (!f.city.trim()) return 'City required.';
  if (!(f.state || '').match(/^[A-Za-z]{2}$/)) return 'State must be a 2-letter code.';
  if (!(f.zip || '').match(/^\d{5}(-\d{4})?$/)) return 'ZIP must look like 12345 or 12345-6789.';
  return null;
}

function AddressForm({ form, setForm, onCancel, onSave, saving }) {
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  return (
    <div className="w-full space-y-2" data-testid="saved-address-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Label</Label>
          <Input value={form.label} onChange={set('label')} placeholder="Home"
                 data-testid="saved-address-input-label" />
        </div>
        <div>
          <Label className="text-xs">Apartment (optional)</Label>
          <Input value={form.apartment} onChange={set('apartment')} placeholder="Apt 4B"
                 data-testid="saved-address-input-apartment" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Street</Label>
        <Input value={form.street} onChange={set('street')} placeholder="123 Main St"
               data-testid="saved-address-input-street" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <Label className="text-xs">City</Label>
          <Input value={form.city} onChange={set('city')} placeholder="Reston"
                 data-testid="saved-address-input-city" />
        </div>
        <div>
          <Label className="text-xs">State</Label>
          <Input value={form.state} onChange={set('state')} maxLength={2} placeholder="VA"
                 data-testid="saved-address-input-state" />
        </div>
      </div>
      <div>
        <Label className="text-xs">ZIP</Label>
        <Input value={form.zip} onChange={set('zip')} placeholder="20190"
               data-testid="saved-address-input-zip" />
      </div>
      <div>
        <Label className="text-xs">Delivery instructions (optional)</Label>
        <Input value={form.delivery_instructions} onChange={set('delivery_instructions')}
               placeholder="Leave at door" data-testid="saved-address-input-instructions" />
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.is_default}
               onChange={e => setForm(prev => ({ ...prev, is_default: e.target.checked }))}
               data-testid="saved-address-input-default" />
        Make this my default
      </label>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={onSave} disabled={saving} data-testid="saved-address-save-btn">
          <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="saved-address-cancel-btn">
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}
